<template>
<main class="lg:mb-[600px] relative z-10 bg-backgroundBody dark:bg-dark">
  <!--=====================================
       Page Header Section
  =====================================-->
  <section class="pt-28 sm:pb-28 max-md:pb-16 md:py-[155px] lg:py-[177px] relative z-50 overflow-hidden">
    <div id="hero-gradient-wrapper" class="absolute w-full h-full top-0 left-0 -z-10 blur-[35px] md:blur-[60px] scale-75">
      <!-- Gradient Background Image: Positioned centrally with responsive sizing -->
      <img src="@/assets/images/hero-gradient-background.png" alt="hero-gradient-background" id="hero-gradient" class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
    </div>

    <!-- Content Container -->
    <div class="container">
      <!-- Centered Content with Animation -->
      <div class="text-center reveal-me">
        <!-- Badge Component -->
        <div class="rv-badge">
          <span class="rv-badge-text">Project case study</span>
        </div>

        <!-- Title and Description -->
        <h1 class="mt-3.5 sm:mt-5 lg:mt-9 mb-3 sm:mb-4 lg:mb-7">
          Blinggram: Social Connect for Dubai
        </h1>
        <p>
          Introducing Blinggram – a dynamic social media platform designed to forge powerful connections in Dubai. By harnessing visually engaging content, real-time interaction, and innovative features, Blinggram creates a vibrant digital community that amplifies influencer culture and fuels trending conversations.
        </p>
      </div>
    </div>
  </section>

  <!--=====================================
       Documentation Section
  =====================================-->
  <section class="pb-14 md:pb-16 lg:pb-[88px] xl:pb-[100px]">
    <div class="max-w-[1440px] mx-auto px-7 md:px-20">
      <div class="flex flex-col gap-x-8 gap-y-12 lg:flex-row">
        <!-- Sticky Table of Contents Sidebar -->
        <aside class="min-w-[275px] lg:mx-auto flex-1">
          <div class="sticky top-40">
            <div class="reveal-me">
              <h3 class="max-md:text-[40px] md:text-[32px]">Table of contents</h3>
              <ul class="mt-7 md:mt-10 [&>*:not(:last-child)]:mb-3 md:[&>*:not(:last-child)]:mb-5">
                <li>
                  <a href="#project-overview" class="text-xl normal-case font-normal leading-7 tracking-normal text-[#000000b3] dark:text-dark-100 hover:font-medium hover:text-secondary dark:hover:text-backgroundBody transition-all lenis-scroll-to">
                    Learn about the project - Project Overview
                  </a>
                </li>
                <li>
                  <a href="#designing" class="text-xl normal-case font-normal leading-7 tracking-normal text-[#000000b3] dark:text-dark-100 hover:font-medium hover:text-secondary dark:hover:text-backgroundBody transition-all lenis-scroll-to">
                    Designing the User Experience
                  </a>
                </li>
                <li>
                  <a href="#architecture" class="text-xl normal-case font-normal leading-7 tracking-normal text-[#000000b3] dark:text-dark-100 hover:font-medium hover:text-secondary dark:hover:text-backgroundBody transition-all lenis-scroll-to">
                    Building the information architecture
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </aside>

        <!-- Main Content Area -->
        <article class="project-details-body">
          <figure class="reveal-me">
            <img src="@/assets/images/services/blingram-1.png" alt="Services Big Img" />
          </figure>
          <div class="reveal-me">
            <h3 id="project-overview">
              Learn about the project - Project Overview
            </h3>
            <p>
              Blinggram is an innovative social media platform tailored for Dubai’s fast-paced digital landscape. It connects users through stunning visual content and interactive features, fostering a vibrant community where influencers, brands, and everyday users engage and share trending moments.
              <br /><br />
              The platform is designed to boost digital conversations and elevate social influence through seamless integration of real-time updates and community-driven features.
            </p>
          </div>
          <div class="reveal-me">
            <h3 id="designing">Designing the User Experience</h3>
            <p>
              The design of Blinggram prioritizes dynamic engagement and visual appeal. Intensive research into user behavior and social trends in Dubai led to an interface that emphasizes connectivity, interactive content, and real-time engagement.
              <br /><br />
              Every element—from feed layout to interactive buttons—has been crafted to foster an immersive experience that encourages users to connect, share, and amplify their digital presence.
            </p>
            <img src="@/assets/images/services/blingram-process.png" alt="Project details Button Img" />
          </div>
          <div class="reveal-me">
            <h3 id="architecture">Building the information architecture</h3>
            <p>
              Blinggram’s architecture is built to support high-volume social interactions and real-time content sharing. The platform organizes user profiles, posts, and interactive features in a clear, scalable structure that simplifies navigation.
              <br /><br />
              Advanced algorithms and intuitive menus ensure users can effortlessly explore trending content, connect with influencers, and engage with the community, all while maintaining a smooth and secure digital experience.
            </p>
            <img id="final-design" src="@/assets/images/services/blingram-2.png" alt="Business Card" />
          </div>
        </article>
      </div>
    </div>
  </section>

  <section class="pt-14 md:pt-16 lg:pt-[88px] xl:pt-[100px] pb-14 md:pb-16 lg:pb-[88px] xl:pb-[100px] relative">
    <div class="absolute left-1/2 scale-y-75 scale-x-[1.8] top-1/2 -translate-y-1/2 -translate-x-1/2">
      <img src="@/assets/images/gradient-bg.png" alt="">
    </div>
    <div class="container">
      <!-- Section Heading -->
      <h2 class="text-[46px] sm:text-5xl md:text-[52px] max-md:text-left max-lg:leading-[1.33] lg:text-7xl font-normal !leading-[1.3] lg:tracking-[-2.88px] mb-9 xl:mb-[50px] text-appear">
        <div class="line" style="display: block; text-align: start; width: 100%;">
          <div class="word" style="display: inline-block; transform: translate(0px, 0px);">Next</div> 
          <div class="word" style="display: inline-block; transform: translate(0px, 0px);margin-left: 10px;">Project</div>
        </div>
      </h2>
      <router-link to="/pairboard">
        <div class="flex flex-col md:flex-row items-center gap-10 reveal-me underline-hover-effect group" style="transform: translate(0px, 0px); opacity: 1;">
          <!-- Project Image -->
          <figure class="md:max-w-[372px] max-md:w-full md:h-[328px] overflow-hidden">
            <img src="@/assets/images/services/services-1.png" alt="" class="w-full h-full object-cover transition-all duration-500 group-hover:scale-125 group-hover:rotate-3">
          </figure>

          <!-- Project Details -->
          <div class="self-start md:self-center">
            <!-- Project Label -->
            <p class="uppercase text-xs text-secondary dark:text-backgroundBody font-poppins font-normal leading-6 tracking-[8px]">
              Project
            </p>

            <!-- Project Title Link -->
            <div class="blog-title mb-1">
              <h3 class="text-4xl md:text-[56px] md:leading-[1.2] md:tracking-[-1.68px] mt-6 mb-4">
                PairBoard
              </h3>
            </div>

            <!-- Project Year -->
            <p class="text-2xl text-[#000000b3] dark:text-dark-100 leading-[1.4] tracking-[0.48px]">
              /2024
            </p>
          </div>
        </div>
      </router-link>
    </div>
  </section>
  <!--=====================================
       CTA Section
  =====================================-->
  <section
  class="pt-14 md:pt-16 lg:pt-[88px] xl:pt-[100px] pb-[70px] lg:pb-[140px] relative"
>
  <div class="container">
    <!-- CTA Heading -->
    <h2
      class="text-center xl:text-[96px] font-normal xl:leading-[1.1] xl:tracking-[-2.88px]"
    >
      Got Questions?
      <span class="font-instrument sm:mt-10 italic block max-md:inline-block"
        >We’ve Got Answers</span
      >
    </h2>

    <ul
    class="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-6 list-none"
  >
      <!-- Primary CTA Button -->
      <li
        class="block md:inline-block w-[90%] mx-auto md:ml-auto md:mr-0 md:w-auto"
      >
      
      </li>
      <!-- Secondary CTA Button -->
      <li
        class="block md:inline-block w-[90%] mx-auto md:mr-auto md:ml-0 md:w-auto"
      >
        <router-link
          to="/contact"
          class="rv-button rv-button-primary block md:inline-block"
        >
          <div class="rv-button-top contact-btn-lg">
            <span>Let's Connect</span>
          </div>
          <div class="rv-button-bottom contact-btn-lg">
            <span>Let's Connect</span>
          </div>
        </router-link>
      </li>
    </ul>
  </div>
</section>
</main>
<!--=====================================
     Footer Section
======================================-->
<FooterComponent/>


</template>
<script>
import FooterComponent from '@/components/FooterComponent.vue'
export default {
  name: 'BlingGram',
  components: {
    FooterComponent,
  }
}
</script>